import React from 'react';

import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import UseToast from 'hooks/use-toast';
import UseModal from 'hooks/use-modal';
import useAction from 'hooks/use-action';

import { CourseAddToCart } from 'services/payments';
import { CourseModule, JobQualificationWaitingList } from 'services/core';

import LoginPrompt from 'components/common/loginprompt/LoginPrompt';
import ContentCertificate from 'components/common/contentcertificate/ContentCertificate';
import CourseCurriculum from '../coursecurriculum/CourseCurriculum';
import Modal from '@core/components/Modal/Modal';
import ActionButton from '@core/components/actionbutton/ActionButton';

import ShopIcon from '@core/assets/icons/shop-icon.svg';
// import CloseSquare from '@core/assets/icons/close-square.svg';

import {
	Lacking,
	Purchasing,
	Ready,
	Completed,
	Progressing,
	ComingSoon
} from 'constants/content-status';
import { CART_REFETCH_ACTION } from 'constants/Actions';

import classes from './CourseMainButton.module.scss';
import { MOBILE_FULLSCREEN } from '@core/constants/ModalTypes';

const CourseMainButton = ({ course_id, status, refetchOverview, kind }) => {
	const { toast, ToastContainer } = UseToast();
	const [actionHandler] = useAction();
	const router = useRouter();
	const {
		show: curriculumModalVisible,
		showModal: curriculumShowModal,
		destroyModal: curriculumDestroyModal
	} = UseModal();
	const {
		show: certificateModalVisible,
		showModal: certificateShowModal,
		destroyModal: certificateDestroyModal
	} = UseModal();
	const {
		show: loginNoticeVisible,
		showModal: loginNoticeShowModal,
		destroyModal: loginNoticeDestroyModal
	} = UseModal();
	const { mutate: callAddToCart, isLoading: addToCardLoading } = useMutation(CourseAddToCart, {
		onSuccess: (data) => {
			refetchOverview();
			toast(
				<div className="d-flex gap-3">
					<ShopIcon /> {data.data?.message}
				</div>
			);
			actionHandler({ type: CART_REFETCH_ACTION });
		},
		onError: (error) => {
			if (error.response.status === 401) {
				loginNoticeShowModal();
			} else {
				toast(error.response.data.data, 'Alert');
			}
		}
	});
	const { mutate: callAddToWaitingList, isLoading: addToWaitingListLoading } = useMutation(
		JobQualificationWaitingList.POST,
		{
			onSuccess: (data) => {
				refetchOverview();
				toast(
					<div className="d-flex gap-3">
						<ShopIcon /> {data.data?.message}
					</div>
				);
			},
			onError: (error) => {
				if (error.response.status === 401) {
					loginNoticeShowModal();
				} else {
					toast(error.response.data.data, 'Alert');
				}
			}
		}
	);
	const { mutate: callCourseModule, isLoading: courseModuleLoading } = useMutation(CourseModule, {
		onSuccess: (data) => {
			const { launch_link, show_curriculum } = data.data;
			if (launch_link) {
				setTimeout(() => window?.open(launch_link), 0);
			} else if (show_curriculum) {
				handleShowCurriculum();
			}
		},
		onError: (error) => {
			toast(error.response.data.data, 'Alert');
		}
	});

	const handleShowCurriculum = () => {
		curriculumShowModal();
	};
	const buttonTypeHandler = () => {
		switch (status) {
			case Lacking:
				return (
					<ActionButton
						disabled={addToCardLoading}
						loading={addToCardLoading}
						onClick={() => callAddToCart({ id: course_id })}
						className={`${classes['content-button']}`}>
						Add to cart
					</ActionButton>
				);
			case Purchasing:
				return (
					<ActionButton
						onClick={() => router.push('/cart')}
						className={`${classes['content-button']}`}>
						Finalize purchase...
					</ActionButton>
				);
			case Ready:
				return (
					<ActionButton
						disabled={courseModuleLoading}
						loading={courseModuleLoading}
						onClick={() => callCourseModule(course_id)}
						className={`${classes['content-button']}`}>
						Start learning now!
					</ActionButton>
				);
			case Progressing:
				return (
					<>
						<ActionButton
							kind={kind ? kind : "primary"}
							disabled={courseModuleLoading}
							loading={courseModuleLoading}
							onClick={() => callCourseModule(course_id)}
							className={`${classes['content-button-half']}`}>
							Continue learning
						</ActionButton>
						<ActionButton
							kind="blank"
							disabled={courseModuleLoading}
							loading={courseModuleLoading}
							onClick={() => handleShowCurriculum()}
							className={`${classes['content-button-half']} py-4`}>
							Start from beginning
						</ActionButton>
					</>
				);
			case Completed:
				return (
					<ActionButton
						disabled={courseModuleLoading}
						loading={courseModuleLoading}
						onClick={() => certificateShowModal()}
						className={`${classes['content-button']}`}>
						Receive certificate!
					</ActionButton>
				);
			case ComingSoon:
				return (
					<ActionButton
						disabled={addToWaitingListLoading}
						loading={addToWaitingListLoading}
						onClick={() => callAddToWaitingList(course_id)}
						className={`${classes['content-button']}`}>
						Join waiting list
					</ActionButton>
				);
		}
	};

	return (
		<>
			<ToastContainer />
			<Modal title="Curriculum" closeHandler={curriculumDestroyModal} show={curriculumModalVisible}>
				<CourseCurriculum section="lectures" />
			</Modal>
			<Modal
				type={MOBILE_FULLSCREEN}
				closeHandler={certificateDestroyModal}
				show={certificateModalVisible}>
				<div
					className={`${classes['certificate-modal']} d-flex flex-column justify-content-center`}>
					<ContentCertificate content_type="course" />
				</div>
			</Modal>
			<Modal
				type={MOBILE_FULLSCREEN}
				closeHandler={loginNoticeDestroyModal}
				show={loginNoticeVisible}>
				<div
					className={`${classes['login-notice-modal']} d-flex flex-column justify-content-center container`}>
					<div className={`d-flex flex-column align-items-center justify-content-center`}>
						<LoginPrompt />
					</div>
				</div>
			</Modal>
			{buttonTypeHandler()}
		</>
	);
};

export default React.memo(CourseMainButton);
